import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { CatchBoundary, createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { type ReactNode } from 'react';
import { Focusable, TooltipTrigger } from 'react-aria-components';
import { pick as rPick } from 'remeda';
import {
  number as vNumber,
  parse as vParse,
  pipe as vPipe,
  string as vString,
  transform as vTransform,
} from 'valibot';

import { SecondaryActionsIconButton } from '@/components/secondary-actions-icon-button.component';
import { theme } from '@/constants/theme';
import { CampaignRequestEditStatusEnum, CampaignRequestStatusEnum } from '@/libs/openapi';
import {
  ArchiveStrokeIcon,
  Badge,
  Breadcrumb,
  Breadcrumbs,
  CopyStrokeIcon,
  EditStrokeIcon,
  Icon,
  IconButton,
  MenuItem,
  PauseCircleSrokeStrokeIcon,
  SNACKBAR_SLOTS,
  Snackbar,
  Text,
  TOAST_TIMEOUT,
  Tooltip,
  View,
} from '@/libs/serenity-ui';
import { RouterLink } from '@/libs/tanstack-router';
import { auditLogQueries } from '@/resources/audit-log/audit-log.api';
import { auditLogPolicies } from '@/resources/audit-log/audit-log.policies';
import { campaignEditRequestQueries } from '@/resources/campaign-edit-request/campaign-edit-request.api';
import { campaignEditRequestPolicies } from '@/resources/campaign-edit-request/campaign-edit-request.policies';
import { campaignRequestMutations, campaignRequestQueries } from '@/resources/campaign-request/campaign-request.api';
import { campaignRequestPolicies } from '@/resources/campaign-request/campaign-request.policies';
import { campaignRequestCommentPolicies } from '@/resources/campaign-request-comment/campaign-request-comment.policies';
import { organizationTeamPolicies } from '@/resources/organization-team/organization-team.policies';
import { CampaignRequestChatWidget } from '@/routes/_main/-components/campaign-request-chat-widget.component';
import { CustomerAuditLogWidget } from '@/routes/_main/_details/campaigns/-components/customer-audit-log-widget.component';
import { DetailsWidget } from '@/routes/_main/_details/campaigns/-components/details-widget.component';
import { EditRequestsWidget } from '@/routes/_main/_details/campaigns/-components/edit-requests-widget.component';
import { SettingsWidget } from '@/routes/_main/_details/campaigns/-components/settings-widget.component';
import { useCampaignRequestNotification } from '@/routes/_main/_details/campaigns/-hooks/use-campaign-request-notification.hook';
import { useEditRequestNotification } from '@/routes/_main/_details/campaigns/-hooks/use-edit-request-notification.hook';
import { CopyIdToolbarTextButton } from '@/routes/_main/_form/-components/copy-id-toolbar-text-button.component';
import { Resource } from '@/types/resource.type';
import { timezone } from '@/utils/timezone.util';

export const Route = createFileRoute('/_main/_details/campaigns/$campaignId')({
  component: RouteComponent,
  params: {
    parse: ({ campaignId }) => ({ campaignId: vParse(vPipe(vString(), vTransform(Number), vNumber()), campaignId) }),
    stringify: ({ campaignId }) => ({ campaignId: String(campaignId) }),
  },
  staticData: {
    details: {
      heading: HeadingComponent,
      pageType: 'Campaign',
      slots: {
        topBarComponent: TopBarComponent,
        idComponent: IdComponent,
        statusComponent: StatusComponent,
        actionsComponent: ActionsComponent,
      },
    },
  },
  beforeLoad: async ({ context, params }) => {
    if (typeof (params.campaignId as unknown) !== 'number') {
      throw notFound({ routeId: '__root__' });
    }

    if (context.user && !context.can.campaignRequests.view()) {
      throw redirect({ to: '/' });
    }

    const detail = await context.queryClient.ensureQueryData(campaignRequestQueries.detail(params.campaignId));
    const campaignTimezone = detail.timezone ?? timezone.get();

    return {
      resource: Resource.campaignRequest as const,
      queryOptions: {
        detail: campaignRequestQueries.detail(params.campaignId),
        editRequestList: campaignEditRequestQueries.list(params.campaignId, {
          sorting: { id: 'createdAt', desc: true },
          campaignTimezone,
        }),
        auditLogList: auditLogQueries.list({ id: params.campaignId, resource: Resource.campaignRequest, limit: 10, page: 1 }),
      },
      can: {
        [Resource.campaignRequest as const]: rPick(campaignRequestPolicies.bindContext({ user: context.user.original, organization: context.organization?.original }), [ 'create', 'update', 'partialUpdate', 'archive', 'unarchive' ]),
        [Resource.campaignEditRequest as const]: rPick(campaignEditRequestPolicies.bindContext({ user: context.user.original, organization: context.organization?.original }), [ 'create', 'update' ]),
        [Resource.campaignRequestComment as const]: rPick(campaignRequestCommentPolicies.bindContext({ user: context.user.original, organization: context.organization?.original }), [ 'create' ]),
        [Resource.organizationTeam as const]: rPick(organizationTeamPolicies.bindContext({ user: context.user.original }), [ 'create' ]),
        [Resource.activityLog as const]: rPick(auditLogPolicies.bindContext({ user: context.user.original }), [ 'view' ]),
      },
    };
  },
  loader: async ({ context }) => {
    if (!context.user) {
      return undefined;
    }

    const detail = await context.queryClient.ensureQueryData(context.queryOptions.detail);
    context.queryClient.prefetchQuery(context.queryOptions.editRequestList);
    if (context.can.activityLog.view()) {
      context.queryClient.prefetchQuery(context.queryOptions.auditLogList);
    }

    return { detail };
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `TrendMD | ${loaderData?.detail.campaignName ?? 'Campaign details'}`,
      },
    ],
  }),
});

function RouteComponent() {
  const routeParams = Route.useParams();
  const routeContext = Route.useRouteContext();

  const { show, variant, message, dismiss } = useEditRequestNotification(routeParams.campaignId);
  const {
    show: showCampaignNotification,
    variant: campaignVariant,
    message: campaignMessage,
    dismiss: dismissCampaign,
  } = useCampaignRequestNotification(routeParams.campaignId);

  return (
    <>
      <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
        <CampaignRequestChatWidget
          campaignRequestId={routeParams.campaignId}
          canCreate={routeContext.can.campaignRequestComment.create()}
        />
      </CatchBoundary>

      {showCampaignNotification && campaignVariant != null && (
        <Snackbar variant={campaignVariant}>
          <Icon />
          <Text>{campaignMessage}</Text>
          <IconButton slot={SNACKBAR_SLOTS.CLOSE} onPress={dismissCampaign} />
        </Snackbar>
      )}

      {show && variant != null && (
        <Snackbar variant={variant}>
          <Icon />
          <Text>{message}</Text>
          <IconButton slot={SNACKBAR_SLOTS.CLOSE} onPress={dismiss} />
        </Snackbar>
      )}

      <View>
        <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
          <DetailsWidget />
        </CatchBoundary>
        <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
          <SettingsWidget />
        </CatchBoundary>
      </View>

      <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
        <EditRequestsWidget />
      </CatchBoundary>
      {routeContext.can.activityLog.view() &&
        <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
          <CustomerAuditLogWidget />
        </CatchBoundary>}
    </>
  );
}

function TopBarComponent(): ReactNode {
  return (
    <Breadcrumbs>
      <Breadcrumb>
        <RouterLink to="/campaigns">Campaigns</RouterLink>
      </Breadcrumb>
      <Breadcrumb hidden>
        <RouterLink to=".">Campaign</RouterLink>
      </Breadcrumb>
    </Breadcrumbs>
  );
}

function HeadingComponent(): ReactNode {
  const routeContext = Route.useRouteContext();
  const detailQuery = useSuspenseQuery(routeContext.queryOptions.detail);

  return detailQuery.data.original.campaignName;
}

function StatusComponent(): ReactNode {
  const routeContext = Route.useRouteContext();
  const detailQuery = useSuspenseQuery(routeContext.queryOptions.detail);
  const editRequestListQuery = useQuery(routeContext.queryOptions.editRequestList);

  const status = detailQuery.data.transformed.forForm.status;

  const pendingPauseEdit = editRequestListQuery.data?.data.find((row) =>
    !!row.original.pauseCampaignReason &&
    row.original.status === CampaignRequestEditStatusEnum.IN_REVIEW) ?? null;

  if (pendingPauseEdit) {
    return (
      <TooltipTrigger>
        <Focusable>
          <Badge {...theme.badge[status]} size={32}>
            <PauseCircleSrokeStrokeIcon color="orange" />
            <Text>{status}</Text>
          </Badge>
        </Focusable>
        <Tooltip placement="bottom">Change to Pause in review</Tooltip>
      </TooltipTrigger>
    );
  }

  return <Badge {...theme.badge[status]} size={32}>{status}</Badge>;
}

function IdComponent(): ReactNode {
  const routeParams = Route.useParams();

  return <CopyIdToolbarTextButton id={routeParams.campaignId} />;
}

function ActionsComponent(): ReactNode {
  const routeParams = Route.useParams();
  const routeContext = Route.useRouteContext();
  const navigate = Route.useNavigate();

  const detailQuery = useSuspenseQuery({ ...routeContext.queryOptions.detail, select: undefined });
  const editRequestListQuery = useSuspenseQuery({ ...routeContext.queryOptions.editRequestList, select: undefined });

  const archiveMutation = useMutation(campaignRequestMutations.archive);

  return (
    <SecondaryActionsIconButton id={routeParams.campaignId}>
      {routeContext.can.campaignRequest.update(detailQuery.data) &&
        <MenuItem onAction={() => navigate({ to: '/campaigns/$campaignId/edit', params: { campaignId: routeParams.campaignId }})}>
          <EditStrokeIcon />
          <Text>Edit</Text>
        </MenuItem>}
      {(() => {
        const editRequests = editRequestListQuery.data.items;
        const campaignRequest = detailQuery.data;
        const campaignStatus = campaignRequest.status;

        if (routeContext.can.campaignEditRequest.create({ editRequests, campaignRequest })) {
          return (
            <MenuItem onAction={() => navigate({ to: '/campaigns/$campaignId/edit/request', params: { campaignId: routeParams.campaignId }})}>
              <EditStrokeIcon />
              <Text>Request edit</Text>
            </MenuItem>
          );
        }

        const inReviewEdit = editRequests.find(({ status }) => status === CampaignRequestEditStatusEnum.IN_REVIEW);
        if (inReviewEdit && campaignStatus !== CampaignRequestStatusEnum.PAUSED && routeContext.can.campaignEditRequest.update(inReviewEdit)) {
          return (
            <MenuItem onAction={() => navigate({ to: '/campaigns/$campaignId/edit/request', params: { campaignId: routeParams.campaignId }})}>
              <EditStrokeIcon />
              <Text>Request edit</Text>
            </MenuItem>
          );
        }

        if (campaignStatus === CampaignRequestStatusEnum.PROCESSING) {
          return (
            <MenuItem onAction={() => routeContext.toastQueue.add(
              {
                message: "We're currently processing your campaign. You'll be able to submit an edit request once the campaign is active.",
                variant: 'info',
              },
              { timeout: TOAST_TIMEOUT },
            )}>
              <EditStrokeIcon />
              <Text>Request edit</Text>
            </MenuItem>
          );
        }

        return null;
      })()}

      {routeContext.can.campaignRequest.create() &&
        <MenuItem onAction={() => navigate({ to: '/campaigns/duplicate/$campaignId', params: { campaignId: routeParams.campaignId }})}>
          <CopyStrokeIcon />
          <Text>Duplicate</Text>
        </MenuItem>}

      {routeContext.can.campaignRequest.archive(detailQuery.data) &&
        <MenuItem
          onAction={() => {
            archiveMutation.mutate({ id: routeParams.campaignId, archived: true }, {
              onSuccess: () => {
                routeContext.toastQueue.add({ message: 'Campaign archived.', variant: 'success' }, { timeout: TOAST_TIMEOUT });
                navigate({ to: '/campaigns' });
              },
              onError: (error) => routeContext.toastQueue.add({ message: error.message, variant: 'error' }, { timeout: TOAST_TIMEOUT }),
            });
          }}>
          <ArchiveStrokeIcon />
          <Text>Archive</Text>
        </MenuItem>}

      {routeContext.can.campaignRequest.unarchive(detailQuery.data) &&
        <MenuItem
          onAction={() => {
            archiveMutation.mutate({ id: routeParams.campaignId, archived: false }, {
              onSuccess: () => {
                routeContext.toastQueue.add({ message: 'Campaign unarchived.', variant: 'success' }, { timeout: TOAST_TIMEOUT });
                navigate({ to: '/campaigns/archive' });
              },
              onError: (error) => routeContext.toastQueue.add({ message: error.message, variant: 'error' }, { timeout: TOAST_TIMEOUT }),
            });
          }}>
          <ArchiveStrokeIcon />
          <Text>Unarchive</Text>
        </MenuItem>}
    </SecondaryActionsIconButton>
  );
}
